"use client";

import { useMemo, useState } from "react";
import SolarIcon from "@/components/ui/SolarIcon/SolarIcon";
import {
    Avancer,
    Curseur,
    Ecran,
    Montant,
    Question,
    Recommencer,
    Resultat,
    euros,
    eurosPrecis,
    remplir,
    useParcours,
} from "./Journey";
import {
    ABATTEMENT_MINIMUM,
    ACTIVITES,
    indemniteJournaliere,
    IJ,
} from "@/lib/tools/incomeTax";
import { PLAFONDS } from "@/lib/tools/microEnterprise";
import { expliquerIndemnitesJournalieres } from "@/lib/tools/explanations";
import { IndiceExplication, MontantExplicable, PanneauExplication, useExplications } from "./Explanation";
import { useDossierOutil, useSignalerDossier } from "./ToolsDossier";
import { reprendre } from "@/lib/tools/dossier";

/**
 * Combien touche un micro-entrepreneur en arrêt maladie.
 *
 * Le calcul officiel se publie en revenu annuel moyen, ce qui le rend
 * inutilisable : un micro-entrepreneur connaît son chiffre d'affaires, pas son
 * revenu au sens de la Sécurité sociale. Or le second est le premier diminué
 * de l'abattement forfaitaire, ce qui change tout : à 30 000 € encaissés, un
 * libéral touche 27,12 € par jour et un vendeur 11,92 €, pour le même travail
 * et le même chiffre d'affaires.
 *
 * L'outil part donc du chiffre d'affaires et montre la conversion, plutôt que
 * de demander une donnée que personne n'a sous la main.
 *
 * TROIS PORTES FERMÉES, TROIS REFUS MOTIVÉS. Profession libérale réglementée :
 * le référentiel ne couvre que le régime général, et l'outil le dit avant de
 * chiffrer quoi que ce soit. Moins de douze mois d'affiliation : la question
 * est posée avant le chiffre d'affaires, parce qu'aucun chiffre d'affaires ne
 * rouvre cette porte, mais le lecteur qui n'a pas encore créé peut tout de
 * même voir ce qu'il toucherait une fois le délai passé, puisque c'est la
 * question qu'il est venu poser. Revenu retenu sous le minimum : le refus
 * arrive dès le chiffre d'affaires, sans poser la question de la durée qui ne
 * servirait à rien, et donne le chiffre d'affaires qu'il faudrait dans cette
 * activité, seule façon utile d'exprimer un seuil publié en revenu.
 */

const PHASES = {
    activite: 0,
    refusCipav: 0,
    affiliation: 0,
    refusAffiliation: 0,
    ca: 1,
    refusRevenu: 1,
    duree: 2,
    resultat: 2,
};

/** Les durées d'arrêt proposées en cartes, en jours. */
const DUREES = [7, 15, 30, 60];

/**
 * Borne haute du curseur de durée. C'est une borne d'exploration, pas une
 * règle : ni le référentiel ni la lib ne fixent de durée maximale
 * d'indemnisation, et l'outil n'en affirme aucune.
 */
const DUREE_LIBRE_MAXIMUM = 180;

/** Pas du curseur de chiffre d'affaires, qui en est aussi la borne basse. */
const CA_PAS = 500;

/**
 * Borne haute du curseur de chiffre d'affaires : le plafond du régime, arrondi
 * au pas inférieur pour que le pouce et la valeur affichée coïncident.
 */
function caMaximum(activite) {
    return Math.floor(PLAFONDS[activite] / CA_PAS) * CA_PAS;
}

/** Vrai quand c'est le minimum d'abattement, et non le taux, qui s'applique. */
function plancherAbattement(ca, activite) {
    return ca * ACTIVITES[activite].abattement < ABATTEMENT_MINIMUM;
}

const L = {
    fr: {
        phases: ["Votre situation", "Votre chiffre d'affaires", "Votre arrêt"],
        retour: "Retour",
        continuer: "Continuer",
        recommencer: "Refaire le calcul",

        qActivite: "Quelle est votre activité ?",
        qActiviteAide:
            "Elle fixe votre abattement forfaitaire, et c'est lui qui décide de votre indemnité : la Sécurité sociale ne retient pas ce que vous encaissez, mais ce qui reste après cet abattement.",
        vente: "Vente de marchandises",
        venteDetail: "Achat-revente, hôtellerie, restauration",
        services: "Prestations de services",
        servicesDetail: "Relevant des BIC : plomberie, esthétique, chambres d'hôtes",
        bnc: "Activité libérale",
        bncDetail: "Relevant des BNC : développement web, traduction, coaching",
        cipav: "Profession libérale réglementée",
        cipavDetail: "Votre retraite relève de la Cipav, comme un géomètre-expert ou un moniteur de ski",

        qAffiliation: "Depuis combien de temps votre activité existe-t-elle ?",
        qAffiliationAide:
            "Il faut douze mois d'inscription continue comme travailleur indépendant, ce que l'Assurance maladie appelle l'affiliation, avant toute indemnisation. En pratique, ce délai court depuis le début de votre activité, pas depuis votre premier arrêt.",
        affiliationMoins: "Moins de douze mois",
        affiliationMoinsDetail: "Ou pas encore créée",
        affiliationPlus: "Douze mois ou plus",
        affiliationPlusDetail: "Sans interruption depuis la création",

        qCa: "Quel est votre chiffre d'affaires annuel moyen ?",
        qCaAide:
            "La Sécurité sociale retient la moyenne de vos trois dernières années civiles. Si votre activité a moins de trois années complètes, indiquez votre moyenne annuelle depuis le début : le résultat sera indicatif.",
        ca: "Chiffre d'affaires annuel moyen",
        caAide: "Ce que vous encaissez, avant tout abattement : la conversion vient après.",

        qDuree: "Combien de temps dure votre arrêt ?",
        qDureeAide:
            "Les trois premiers jours de chaque arrêt ne sont pas indemnisés : sur un arrêt court, cette carence pèse lourd.",
        joursCarte: "{n} jours",
        joursIndemnises: "{n} jours indemnisés après la carence",
        autreDuree: "Une autre durée",
        autreDureeDetail: "Vous la réglez au jour près sur le résultat",
        duree: "Durée de l'arrêt",
        jour: "jour",
        jours: "jours",

        resultatTitre: "Votre indemnité",
        parJour: "Indemnité par jour",
        parJourAide: "Montant brut, avant prélèvements sociaux",
        total: "Vous toucheriez",
        totalAide: "pour un arrêt de {jours} {unite}, dont {indemnises} indemnisés",
        totalAideUn: "pour un arrêt de {jours} {unite}, dont 1 indemnisé",
        totalCarence: "pour un arrêt de {jours} {unite}, entièrement dans la carence : rien n'est versé",
        dureeAide: "Déplacez le curseur : plus l'arrêt est court, plus la carence pèse.",
        carence: "{n} jours de carence, indemnisation à partir du {debut}e jour.",
        prescription:
            "Un arrêt se prescrit par tranches : {initiale} jours au maximum la première fois, {prolongation} jours pour chaque prolongation. C'est pour cela qu'un arrêt long arrive en plusieurs fois plutôt qu'en un seul certificat.",
        dureeInconnue:
            "Le nombre total d'indemnités auquel vous avez droit dépend de votre régime, et aucune page officielle ne le publie de la même façon : demandez-le directement à votre caisse.",
        plafonne:
            "Vous touchez le maximum, {max} par jour : au-delà de {ca} de chiffre d'affaires, encaisser plus ne change plus rien.",
        projection:
            "Ce droit ne s'ouvrira qu'après douze mois d'affiliation continue : d'ici là, l'indemnité est nulle.",

        conversionTitre: "Pourquoi ce montant, et pas votre chiffre d'affaires divisé par 730",
        conversion:
            "L'abattement forfaitaire de votre activité, {abattement} %, ramène vos {ca} à {revenu} de revenu retenu. C'est ce montant que la Sécurité sociale divise par 730.",
        conversionPlafonne:
            "L'abattement forfaitaire de votre activité, {abattement} %, ramène vos {ca} à {revenu} de revenu retenu. Ce revenu est ramené au plafond de {plafond}, et c'est ce plafond que la Sécurité sociale divise par 730.",
        compVente:
            "Un libéral au même chiffre d'affaires toucherait {montant} par jour, {ratio} fois plus que vous, uniquement parce que son abattement est de {bnc} % au lieu de {vente} %.",
        compVentePlafond:
            "Un libéral au même chiffre d'affaires toucherait déjà le maximum, {montant} par jour : son abattement de {bnc} % au lieu de {vente} % le porte au plafond bien avant vous.",
        compServices:
            "Au même chiffre d'affaires, un libéral toucherait {liberal} par jour et un vendeur {vendeur} : seul l'abattement change.",
        compServicesPlafond:
            "Au même chiffre d'affaires, un libéral toucherait déjà le maximum, {liberal} par jour, et un vendeur {vendeur} : seul l'abattement change, et il porte le libéral au plafond avant vous.",
        compBnc:
            "Un vendeur au même chiffre d'affaires toucherait {montant} par jour, {ratio} fois moins que vous, uniquement parce que son abattement est de {vente} % au lieu de {bnc} %.",
        compBncPlafond:
            "Un vendeur au même chiffre d'affaires toucherait {montant} par jour, {ratio} fois moins que vous : vous êtes au plafond, lui en est encore loin, son abattement de {vente} % au lieu de {bnc} % lui laissant bien moins de revenu retenu.",
        compAucunDroit:
            "Un vendeur au même chiffre d'affaires n'ouvrirait aucun droit : il lui faudrait encaisser {ca} par an pour toucher le moindre euro.",
        compPlafond:
            "À ce niveau, toutes les activités sont au plafond : un libéral ou un vendeur au même chiffre d'affaires toucherait exactement la même chose.",

        refusCipavTitre: "Votre profession relève de la Cipav",
        refusCipavMotif:
            "Nous ne calculons l'indemnité que pour le régime général des indépendants.",
        refusCipavSuite:
            "Pour le régime général, qui couvre le commerce, l'artisanat et les activités libérales hors Cipav, nous retenons le revenu après abattement, un plafond de {plafond} et une indemnité maximale de {max} par jour. Nous n'avons pas vérifié à la source les règles des professions réglementées affiliées à la Cipav, et nous ne publions pas de chiffre sans cette vérification : renseignez-vous auprès de l'Assurance maladie, sur votre compte ameli.",

        refusAffiliationTitre: "Votre couverture démarre après un an",
        refusAffiliationMotif:
            "Il faut douze mois d'affiliation continue avant toute indemnisation.",
        refusAffiliationSuite:
            "Tant que cette première année n'est pas écoulée, vous ne touchez rien du tout, quel que soit votre chiffre d'affaires. Vous pouvez tout de même voir ce que vous toucheriez une fois ce délai passé : c'est alors votre chiffre d'affaires qui décidera du montant.",
        refusAffiliationVoir: "Voir quand même ce que je toucherais une fois les douze mois passés",

        refusRevenuTitre: "Vous n'ouvrez aucun droit",
        refusRevenuMotif:
            "Après l'abattement de votre activité, il ne reste que {revenu} de vos {ca} : c'est sous le minimum de {min} par an.",
        refusRevenuSuite:
            "Ce qui reste, que la Sécurité sociale appelle le revenu retenu, est votre chiffre d'affaires diminué de l'abattement forfaitaire de votre activité, {abattement} %. Sous {min}, l'indemnité n'est pas réduite, elle est nulle.",
        refusRevenuSuitePlancher:
            "Ce qui reste, que la Sécurité sociale appelle le revenu retenu, est votre chiffre d'affaires diminué de l'abattement forfaitaire de votre activité, {abattement} %, et cet abattement ne descend jamais sous {plancher}. Sur un chiffre d'affaires aussi petit, c'est ce plancher qui s'applique, et il ne reste presque rien. Sous {min}, l'indemnité n'est pas réduite, elle est nulle.",
        refusRevenuCa:
            "Dans votre activité, il faudrait encaisser au moins {ca} par an en moyenne pour ouvrir le droit.",
        refusRevenuCompare:
            "Un libéral y arrive avec {ca} : le seuil est le même, mais son abattement plus faible lui laisse un revenu retenu plus élevé.",

        note: "Montants bruts, avant prélèvements sociaux. La carence revient à chaque nouvel arrêt : trois arrêts courts dans l'année font neuf jours sans indemnité.",
        source: "Valeurs 2026 relevées sur ameli.fr le 1er septembre 2026.",
    },
    en: {
        phases: ["Your situation", "Your turnover", "Your sick leave"],
        retour: "Back",
        continuer: "Continue",
        recommencer: "Start over",

        qActivite: "What is your activity?",
        qActiviteAide:
            "It sets your standard allowance, and that allowance decides your benefit: social security does not count what you collect, but what is left after it.",
        vente: "Sale of goods",
        venteDetail: "Retail, hotel trade, catering",
        services: "Services",
        servicesDetail: "Commercial (BIC): plumbing, beauty care, guest rooms",
        bnc: "Liberal profession",
        bncDetail: "Non-commercial (BNC): web development, translation, coaching",
        cipav: "Regulated liberal profession",
        cipavDetail: "Your pension is with Cipav, like a chartered surveyor or a ski instructor",

        qAffiliation: "How long has your business existed?",
        qAffiliationAide:
            "You need twelve months of continuous registration as a self-employed worker, what Assurance Maladie calls affiliation, before any benefit is paid. In practice the clock starts when your activity begins, not at your first sick leave.",
        affiliationMoins: "Less than twelve months",
        affiliationMoinsDetail: "Or not created yet",
        affiliationPlus: "Twelve months or more",
        affiliationPlusDetail: "Without interruption since creation",

        qCa: "What is your average annual turnover?",
        qCaAide:
            "Social security uses the average of your last three calendar years. If your activity is less than three full years old, enter your annual average since you started: the result will be indicative.",
        ca: "Average annual turnover",
        caAide: "What you collect, before any allowance: the conversion comes next.",

        qDuree: "How long is your sick leave?",
        qDureeAide:
            "The first three days of each sick leave are unpaid: on a short absence, that waiting period weighs heavily.",
        joursCarte: "{n} days",
        joursIndemnises: "{n} days paid after the waiting period",
        autreDuree: "A different length",
        autreDureeDetail: "You set it to the day on the result",
        duree: "Length of sick leave",
        jour: "day",
        jours: "days",

        resultatTitre: "Your benefit",
        parJour: "Benefit per day",
        parJourAide: "Gross amount, before social levies",
        total: "You would receive",
        totalAide: "for {jours} {unite} of sick leave, {indemnises} of them paid",
        totalAideUn: "for {jours} {unite} of sick leave, 1 of them paid",
        totalCarence: "for {jours} {unite} of sick leave, all within the waiting period: nothing is paid",
        dureeAide: "Move the slider: the shorter the absence, the more the waiting period weighs.",
        carence: "{n} waiting days, paid from day {debut}.",
        prescription:
            "Sick leave is prescribed in blocks: at most {initiale} days the first time, {prolongation} days for each extension. That is why a long absence comes as several certificates rather than one.",
        dureeInconnue:
            "The total number of daily benefits you are entitled to depends on your scheme, and no official page publishes it the same way: ask your health insurance fund directly.",
        plafonne:
            "You receive the maximum, {max} a day: above {ca} of turnover, collecting more changes nothing.",
        projection:
            "This entitlement only opens after twelve continuous months of affiliation: until then, the benefit is nil.",

        conversionTitre: "Why this amount, and not your turnover divided by 730",
        conversion:
            "The standard allowance for your activity, {abattement}%, brings your {ca} down to {revenu} of assessed income. That is the figure social security divides by 730.",
        conversionPlafonne:
            "The standard allowance for your activity, {abattement}%, brings your {ca} down to {revenu} of assessed income. That income is capped at {plafond}, and it is the cap that social security divides by 730.",
        compVente:
            "A liberal professional on the same turnover would receive {montant} a day, {ratio} times as much as you, purely because their allowance is {bnc}% instead of {vente}%.",
        compVentePlafond:
            "A liberal professional on the same turnover would already receive the maximum, {montant} a day: their {bnc}% allowance instead of {vente}% takes them to the cap long before you.",
        compServices:
            "On the same turnover, a liberal professional would receive {liberal} a day and a seller of goods {vendeur}: only the allowance differs.",
        compServicesPlafond:
            "On the same turnover, a liberal professional would already receive the maximum, {liberal} a day, and a seller of goods {vendeur}: only the allowance differs, and it takes the liberal professional to the cap before you.",
        compBnc:
            "A seller of goods on the same turnover would receive {montant} a day; you receive {ratio} times as much, purely because their allowance is {vente}% instead of {bnc}%.",
        compBncPlafond:
            "A seller of goods on the same turnover would receive {montant} a day; you receive {ratio} times as much: you are at the cap, they are still far from it, since a {vente}% allowance instead of {bnc}% leaves them far less assessed income.",
        compAucunDroit:
            "A seller of goods on the same turnover would have no entitlement at all: they would need to collect {ca} a year to receive a single euro.",
        compPlafond:
            "At this level every activity is capped: a liberal professional or a seller of goods on the same turnover would receive exactly the same amount.",

        refusCipavTitre: "Your profession comes under Cipav",
        refusCipavMotif:
            "We only calculate the benefit for the general self-employed scheme.",
        refusCipavSuite:
            "For the general scheme, which covers trade, crafts and liberal activities outside Cipav, we use assessed income after allowance, a {plafond} cap and a maximum benefit of {max} a day. We have not checked the rules for regulated professions attached to Cipav at the source, and we do not publish a figure without that check: ask Assurance Maladie, through your compte ameli.",

        refusAffiliationTitre: "Your cover starts after a year",
        refusAffiliationMotif:
            "Twelve continuous months of affiliation are required before any benefit is paid.",
        refusAffiliationSuite:
            "Until that first year is up, you receive nothing at all, whatever your turnover. You can still see what you would receive once the period has passed: your turnover will then decide the amount.",
        refusAffiliationVoir: "Show me anyway what I would receive once the twelve months have passed",

        refusRevenuTitre: "You have no entitlement",
        refusRevenuMotif:
            "After your activity's allowance, only {revenu} of your {ca} remains: that is below the {min} a year minimum.",
        refusRevenuSuite:
            "What remains, which social security calls assessed income, is your turnover minus the standard allowance for your activity, {abattement}%. Below {min}, the benefit is not reduced, it is nil.",
        refusRevenuSuitePlancher:
            "What remains, which social security calls assessed income, is your turnover minus the standard allowance for your activity, {abattement}%, and that allowance never drops below {plancher}. On such a small turnover it is that floor that applies, and almost nothing is left. Below {min}, the benefit is not reduced, it is nil.",
        refusRevenuCa:
            "In your activity, you would need to collect at least {ca} a year on average to qualify.",
        refusRevenuCompare:
            "A liberal professional gets there with {ca}: the threshold is the same, but a smaller allowance leaves them a higher assessed income.",

        note: "Gross amounts, before social levies. Each new sick leave has its own waiting period: three short absences in a year mean nine days without pay.",
        source: "2026 figures taken from ameli.fr on 1 September 2026.",
    },
};

/** Un rapport entre deux indemnités, avec la virgule de la langue. */
function ratioLisible(v, lang) {
    return v.toFixed(1).replace(".", lang === "fr" ? "," : ".");
}

/** Le taux d'abattement d'une activité, en pourcentage entier. */
function abattementPourcent(activite) {
    return Math.round(ACTIVITES[activite].abattement * 100);
}

/** Les taux des deux activités extrêmes, lus dans la lib et jamais recopiés. */
function tauxTemoins(texte) {
    return remplir(texte, { bnc: abattementPourcent("bnc"), vente: abattementPourcent("vente") });
}

/**
 * Refus motivé : la porte qui s'est fermée, et la règle qui la ferme.
 *
 * Bâti sur `Ecran` pour garder l'en-tête : le retour y est le geste attendu
 * (remonter le chiffre d'affaires, corriger la carte cliquée), et la phase
 * affichée reste celle de la question qui a fermé la porte. Candidat à la
 * consolidation dans Parcours.jsx.
 */
function Refus({
    t,
    titre,
    motif,
    suite,
    complements = [],
    avancer,
    avecNote = true,
    onRecommencer,
    ...ecran
}) {
    return (
        <Ecran
            {...ecran}
            titre={
                <span className="flex items-center gap-2.5">
                    <SolarIcon
                        name="close-circle-linear"
                        className="w-6 h-6 shrink-0 text-amber-600 dark:text-amber-400"
                    />
                    {titre}
                </span>
            }
        >
            <p className="text-base font-semibold dark:text-white mt-4 mb-2">{motif}</p>
            <p className="text-sm text-gray-600 dark:text-lightBlue mb-0">{suite}</p>

            {complements.map((texte) => (
                <p key={texte} className="text-sm text-gray-600 dark:text-lightBlue mt-3 mb-0">
                    {texte}
                </p>
            ))}

            {avancer && <Avancer onClick={avancer.onClick}>{avancer.libelle}</Avancer>}

            {avecNote && (
                <p className="text-xs text-gray-500 dark:text-lightBlue/70 mt-5 mb-0">{t.note}</p>
            )}
            <p className="text-xs text-gray-500 dark:text-lightBlue/70 mt-2 mb-0">{t.source}</p>

            <Recommencer onClick={onRecommencer} libelle={t.recommencer} />
        </Ecran>
    );
}

/**
 * La comparaison entre activités, décidée sur les faits que la lib renvoie.
 *
 * Elle est calculée, jamais affirmée, et le mot « uniquement » n'est employé
 * que lorsque ni le lecteur ni son témoin ne sont au plafond : dès que l'un
 * des deux y est, une part de l'écart vient du plafond et non de l'abattement.
 */
function comparerActivites({ t, lang, r, temoins, activite }) {
    if (activite === "vente") {
        if (r.plafonne && temoins.bnc.plafonne) return t.compPlafond;
        const montant = eurosPrecis(temoins.bnc.montant, lang);
        if (temoins.bnc.plafonne) {
            return tauxTemoins(t.compVentePlafond).replace("{montant}", montant);
        }
        return remplir(tauxTemoins(t.compVente), {
            montant,
            ratio: ratioLisible(temoins.bnc.montant / r.montant, lang),
        });
    }

    if (!temoins.vente.ouvre) {
        return t.compAucunDroit.replace("{ca}", euros(temoins.vente.caMinimum, lang));
    }

    if (activite === "bnc") {
        if (r.plafonne && temoins.vente.plafonne) return t.compPlafond;
        return remplir(tauxTemoins(r.plafonne ? t.compBncPlafond : t.compBnc), {
            montant: eurosPrecis(temoins.vente.montant, lang),
            ratio: ratioLisible(r.montant / temoins.vente.montant, lang),
        });
    }

    if (r.plafonne && temoins.bnc.plafonne && temoins.vente.plafonne) return t.compPlafond;
    return remplir(temoins.bnc.plafonne ? t.compServicesPlafond : t.compServices, {
        liberal: eurosPrecis(temoins.bnc.montant, lang),
        vendeur: eurosPrecis(temoins.vente.montant, lang),
    });
}

/**
 * Résultat chiffré. Les montants qui montent sont des `Montant`, dont le
 * compteur part quand ce composant se monte, c'est-à-dire à l'arrivée sur le
 * résultat.
 */
function ResultatChiffre({
    t,
    lang,
    r,
    temoins,
    activite,
    ca,
    jours,
    projection,
    poserJours,
    onRetour,
    onRecommencer,
    sens,
    reduit,
}) {
    const comparaison = comparerActivites({ t, lang, r, temoins, activite });
    const unite = r.jours === 1 ? t.jour : t.jours;

    // Chaque montant du résultat dit d'où il vient : ses étapes et ses sources.
    const ex = useMemo(
        () => expliquerIndemnitesJournalieres({ chiffreAffairesMoyen: ca, activite }, r, lang),
        [ca, activite, r, lang],
    );
    const { ouverte, basculer } = useExplications();
    const totalAide =
        r.joursIndemnises === 0
            ? t.totalCarence
            : r.joursIndemnises === 1
              ? t.totalAideUn
              : t.totalAide;

    return (
        <Resultat onRetour={onRetour} retour={t.retour} titre={t.resultatTitre} phases={t.phases} sens={sens} reduit={reduit}>
            <IndiceExplication lang={lang} />

            <div className="grid sm:grid-cols-2 gap-3 mt-3">
                <div className="rounded-xl p-5 border border-gray-200/70 dark:border-white/10">
                    <div className="text-xs text-gray-500 dark:text-lightBlue/70 mb-1">
                        {t.parJour}
                    </div>
                    <div className="text-2xl font-bold dark:text-white">
                        <MontantExplicable explication={ex.parJour} ouvert={ouverte === "parJour"} onBasculer={() => basculer("parJour")}>
                            <Montant
                                valeur={r.montant}
                                lang={lang}
                                reduit={reduit}
                                formater={eurosPrecis}
                            />
                        </MontantExplicable>
                    </div>
                    <div className="text-xs text-gray-500 dark:text-lightBlue/70 mt-1">
                        {t.parJourAide}
                    </div>
                    {ouverte === "parJour" && <PanneauExplication explication={ex.parJour} lang={lang} />}
                </div>
                <div className="rounded-xl p-5 border border-primary/40 bg-primary/[0.06]">
                    <div className="text-xs text-gray-500 dark:text-lightBlue/70 mb-1">
                        {t.total}
                    </div>
                    <div className="text-2xl font-bold dark:text-white">
                        <MontantExplicable explication={ex.total} ouvert={ouverte === "total"} onBasculer={() => basculer("total")}>
                            <Montant valeur={r.total} lang={lang} reduit={reduit} />
                        </MontantExplicable>
                    </div>
                    <div className="text-xs text-gray-500 dark:text-lightBlue/70 mt-1">
                        {remplir(totalAide, {
                            jours: r.jours,
                            unite,
                            indemnises: r.joursIndemnises,
                        })}
                    </div>
                    {ouverte === "total" && <PanneauExplication explication={ex.total} lang={lang} />}
                </div>
            </div>

            {projection && (
                <p className="text-sm font-semibold dark:text-white mt-4 mb-0">{t.projection}</p>
            )}

            <div className="mt-6">
                <Curseur
                    libelle={t.duree}
                    aide={t.dureeAide}
                    valeur={jours}
                    poser={poserJours}
                    minimum={1}
                    maximum={DUREE_LIBRE_MAXIMUM}
                    pas={1}
                    formater={(v) => `${v} ${v === 1 ? t.jour : t.jours}`}
                />
            </div>

            <p className="text-sm text-gray-600 dark:text-lightBlue mt-4 mb-0">
                {remplir(t.carence, { n: r.carence, debut: r.carence + 1 })}
            </p>

            <p className="text-sm text-gray-600 dark:text-lightBlue mt-2 mb-0">
                {remplir(t.prescription, {
                    initiale: IJ.prescriptionInitiale,
                    prolongation: IJ.prescriptionProlongation,
                })}
            </p>

            {/* La borne du curseur est une commodité d'exploration : le dire
                vaut mieux que laisser croire à un plafond de droits. */}
            <p className="text-sm text-gray-600 dark:text-lightBlue mt-2 mb-0">{t.dureeInconnue}</p>

            {r.plafonne && (
                <p className="text-sm text-gray-600 dark:text-lightBlue mt-2 mb-0">
                    {remplir(t.plafonne, {
                        ca: euros(r.caPlafond, lang),
                        max: eurosPrecis(IJ.montantMaximal, lang),
                    })}
                </p>
            )}

            <div className="mt-6 pt-5 border-t border-gray-200/70 dark:border-white/10">
                <div className="text-sm font-semibold dark:text-white">{t.conversionTitre}</div>
                <p className="text-sm text-gray-600 dark:text-lightBlue mt-1.5 mb-0">
                    {remplir(r.plafonne ? t.conversionPlafonne : t.conversion, {
                        abattement: abattementPourcent(activite),
                        ca: euros(ca, lang),
                        revenu: euros(r.revenuAnnuelMoyen, lang),
                        plafond: euros(IJ.revenuPlafond, lang),
                    })}
                </p>
                <p className="text-sm text-gray-600 dark:text-lightBlue mt-3 mb-0">
                    {comparaison}
                </p>
            </div>

            <p className="text-xs text-gray-500 dark:text-lightBlue/70 mt-5 mb-0">{t.note}</p>
            <p className="text-xs text-gray-500 dark:text-lightBlue/70 mt-2 mb-0">{t.source}</p>

            <Recommencer onClick={onRecommencer} libelle={t.recommencer} />
        </Resultat>
    );
}

export default function SimulateurIndemnitesJournalieres({ lang = "fr" }) {
    const t = L[lang] || L.fr;

    // Le dossier du lecteur, s'il en a un : les réponses qu'il connaît
    // partent déjà posées, et le parcours s'ouvre sur la première qui manque.
    // La profession réglementée n'est jamais retenue comme activité : l'écran
    // d'activité l'envoie droit au refus, sans la poser.
    const { valeurs: dossier } = useDossierOutil();
    const activiteDossier = dossier.activite && dossier.activite !== "cipav" ? dossier.activite : undefined;
    const [activite, setActivite] = useState(activiteDossier ?? "bnc");
    // Comme le choix de l'activité, le chiffre d'affaires repris se ramène
    // sous le plafond de celle-ci.
    const [ca, setCa] = useState(
        dossier.ca !== undefined ? Math.min(dossier.ca, caMaximum(activiteDossier ?? "bnc")) : 30000,
    );
    const [jours, setJours] = useState(30);
    // Vrai quand le lecteur regarde, sans droit ouvert, ce qu'il toucherait
    // une fois les douze mois d'affiliation passés.
    const [projection, setProjection] = useState(false);

    const { etape, sens, reduit, aller, retour, recommencer, communs } = useParcours(
        "activite",
        () => {
            setActivite("bnc");
            setCa(30000);
            setJours(30);
            setProjection(false);
        },
        PHASES,
        reprendre([
            { etape: "activite", connu: dossier.activite !== undefined },
            // L'affiliation ne se déduit pas du dossier : le parcours s'y arrête.
            dossier.activite === "cipav" ? { etape: "refusCipav" } : { etape: "affiliation", connu: false },
        ]),
    );

    useSignalerDossier({ activite, ca }, etape === "resultat");

    const r = useMemo(
        () => indemniteJournaliere({ chiffreAffairesMoyen: ca, activite, jours }),
        [ca, activite, jours],
    );

    // Les deux activités extrêmes au même chiffre d'affaires : c'est l'écart
    // entre elles que l'outil existe pour montrer.
    const temoins = useMemo(
        () => ({
            bnc: indemniteJournaliere({ chiffreAffairesMoyen: ca, activite: "bnc" }),
            vente: indemniteJournaliere({ chiffreAffairesMoyen: ca, activite: "vente" }),
        }),
        [ca],
    );

    const communsEcran = { ...communs, phases: t.phases, retour: t.retour, onRetour: retour };

    if (etape === "activite") {
        return (
            <Question
                key={etape}
                {...communs}
                phases={t.phases}
                retour={t.retour}
                phase={PHASES.activite}
                titre={t.qActivite}
                aide={t.qActiviteAide}
                choix={["vente", "services", "bnc", "cipav"].map((cle) => ({
                    icone: {
                        vente: "sale-linear",
                        services: "pen-2-linear",
                        bnc: "brain-linear",
                        cipav: "medal-ribbon-star-linear",
                    }[cle],
                    titre: t[cle],
                    detail: t[`${cle}Detail`],
                    onClick: () => {
                        // Le référentiel ne couvre que le régime général : on
                        // le dit avant de chiffrer quoi que ce soit.
                        if (cle === "cipav") {
                            aller("refusCipav");
                            return;
                        }
                        setActivite(cle);
                        // Le plafond du régime dépend de l'activité : un
                        // chiffre d'affaires réglé pour la vente ne tient pas
                        // en libéral.
                        setCa((c) => Math.min(c, caMaximum(cle)));
                        aller("affiliation");
                    },
                }))}
            />
        );
    }

    if (etape === "refusCipav") {
        return (
            <Refus
                key={etape}
                {...communsEcran}
                phase={PHASES.refusCipav}
                t={t}
                titre={t.refusCipavTitre}
                motif={t.refusCipavMotif}
                suite={remplir(t.refusCipavSuite, {
                    plafond: euros(IJ.revenuPlafond, lang),
                    max: eurosPrecis(IJ.montantMaximal, lang),
                })}
                avecNote={false}
                onRecommencer={recommencer}
            />
        );
    }

    if (etape === "affiliation") {
        return (
            <Question
                key={etape}
                {...communsEcran}
                phase={PHASES.affiliation}
                titre={t.qAffiliation}
                aide={t.qAffiliationAide}
                choix={[
                    {
                        icone: "clock-circle-linear",
                        titre: t.affiliationMoins,
                        detail: t.affiliationMoinsDetail,
                        // Aucun chiffre d'affaires ne rouvre cette porte : on
                        // ne demande pas la suite.
                        onClick: () => aller("refusAffiliation"),
                    },
                    {
                        icone: "shield-check-linear",
                        titre: t.affiliationPlus,
                        detail: t.affiliationPlusDetail,
                        onClick: () => {
                            setProjection(false);
                            aller("ca");
                        },
                    },
                ]}
            />
        );
    }

    if (etape === "refusAffiliation") {
        return (
            <Refus
                key={etape}
                {...communsEcran}
                phase={PHASES.refusAffiliation}
                t={t}
                titre={t.refusAffiliationTitre}
                motif={t.refusAffiliationMotif}
                suite={t.refusAffiliationSuite}
                // Le public principal n'a pas encore créé : il repart avec le
                // chiffre qu'il est venu chercher, et la règle reste nommée.
                avancer={{
                    libelle: t.refusAffiliationVoir,
                    onClick: () => {
                        setProjection(true);
                        aller("ca");
                    },
                }}
                onRecommencer={recommencer}
            />
        );
    }

    if (etape === "ca") {
        return (
            <Ecran
                key={etape}
                {...communsEcran}
                phase={PHASES.ca}
                titre={t.qCa}
                aide={t.qCaAide}
            >
                <Curseur
                    libelle={t.ca}
                    aide={t.caAide}
                    valeur={ca}
                    poser={setCa}
                    minimum={CA_PAS}
                    maximum={caMaximum(activite)}
                    pas={CA_PAS}
                    formater={(v) => euros(v, lang)}
                />
                {/* Sous le minimum, la durée ne servirait à rien : on ne la
                    demande pas. */}
                <Avancer onClick={() => aller(r.ouvre ? "duree" : "refusRevenu")}>
                    {t.continuer}
                </Avancer>
            </Ecran>
        );
    }

    if (etape === "refusRevenu") {
        const complements = [t.refusRevenuCa.replace("{ca}", euros(r.caMinimum, lang))];
        if (activite !== "bnc") {
            complements.push(
                t.refusRevenuCompare.replace("{ca}", euros(temoins.bnc.caMinimum, lang)),
            );
        }
        return (
            <Refus
                key={etape}
                {...communsEcran}
                phase={PHASES.refusRevenu}
                t={t}
                titre={t.refusRevenuTitre}
                motif={remplir(t.refusRevenuMotif, {
                    revenu: euros(r.revenuAnnuelMoyen, lang),
                    ca: euros(ca, lang),
                    min: euros(IJ.revenuMinimum, lang),
                })}
                // Sur un tout petit chiffre d'affaires, ce n'est plus le taux
                // qui s'applique mais le minimum d'abattement : la phrase le dit,
                // sinon le lecteur refait le calcul et ne retombe pas dessus.
                suite={remplir(
                    plancherAbattement(ca, activite)
                        ? t.refusRevenuSuitePlancher
                        : t.refusRevenuSuite,
                    {
                        abattement: abattementPourcent(activite),
                        plancher: euros(ABATTEMENT_MINIMUM, lang),
                        min: euros(IJ.revenuMinimum, lang),
                    },
                )}
                complements={complements}
                onRecommencer={recommencer}
            />
        );
    }

    if (etape === "duree") {
        return (
            <Question
                key={etape}
                {...communsEcran}
                phase={PHASES.duree}
                titre={t.qDuree}
                aide={t.qDureeAide}
                choix={[
                    ...DUREES.map((n, i) => ({
                        icone: [
                            "clock-circle-linear",
                            "calendar-linear",
                            "pulse-linear",
                            "file-text-linear",
                        ][i],
                        titre: t.joursCarte.replace("{n}", n),
                        // Le détail est calculé par la lib, pas recopié : c'est
                        // la carence qui fait la différence entre les cartes.
                        detail: t.joursIndemnises.replace(
                            "{n}",
                            indemniteJournaliere({
                                chiffreAffairesMoyen: ca,
                                activite,
                                jours: n,
                            }).joursIndemnises,
                        ),
                        onClick: () => {
                            setJours(n);
                            aller("resultat");
                        },
                    })),
                    {
                        icone: "pen-2-linear",
                        titre: t.autreDuree,
                        detail: t.autreDureeDetail,
                        // La durée se règle sur le résultat, où le curseur
                        // reste manipulable : un écran de plus la poserait
                        // deux fois.
                        onClick: () => aller("resultat"),
                    },
                ]}
            />
        );
    }

    return (
        <ResultatChiffre
            key={etape}
            t={t}
            lang={lang}
            r={r}
            temoins={temoins}
            activite={activite}
            ca={ca}
            jours={jours}
            projection={projection}
            poserJours={setJours}
            onRetour={retour}
            onRecommencer={recommencer}
            sens={sens}
            reduit={reduit}
        />
    );
}
